import { fetchBidHistoryIfNeeded } from './bidHistory';
import { fetchBidLimitBalanceIfNeeded } from './bidLimit';
import { fetchCatalogAnnouncementIfNeeded } from './catalogAnnouncement';
import { fetchCatalogCuratorsIfNeeded } from './catalogCurators';
import { fetchCatalogDetailsIfNeeded } from './catalogDetail/catalogDetail.actions';
import { fetchCatalogItemModels } from './catalogItems';
import { fetchCatalogPresence, fetchLiveCatalogStatusIfNeeded } from './liveCatalogStatus';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalog.actions';
import { fetchCategoryRelatedSearches } from './categoryRelatedSearches';
import { fetchDiscoverPageSuggestions } from './discoverPageSuggestions';
import { fetchItemCardModelIfNeeded, getItemCardModel } from './itemCardModel';
import { fetchItemDetailsIfNeeded, getItemDetail } from './itemDetail';
import { fetchItemFacetsIfNeeded, getItemFacets } from './itemFacets';
import { fetchItemShippingsIfNeeded } from './itemShipping';
import { fetchListingAgentsIfNeeded } from './listingAgent';
import { fetchSavedItemCountsIfNeeded } from './savedItemCount';
import { fetchSellerFollowerCountIfNeeded } from './sellerFollowerCount';
import { fetchSellerRatingSummaryByIdIfNeeded } from './sellerRatings';
import { fetchSellersDetailsIfNeeded } from './sellerDetail';
import { fetchSimilarItemsIfNeeded } from './similarItems';
import { fetchSimilarSoldItemsIfNeeded } from './similarSoldItems';
import { fetchTopSellersIfNeeded } from './topRatedSellers';
import { fetchUpcomingCatalogRegistrationIfNeeded } from './catalogRegistration';
import { getCatalogAddress } from './catalog/catalog.selectors';
import { getFacetIds } from '@/utils/ItemPageUtils';
import { getNotFoundUrl, getUpcomingTitleSearchParams } from '@/utils/urls';
import { loadRelatedGeoAreaByLocation } from '@/redux/modules/geoArea/geoArea';
import { submitIsSavedSearchCheck, submitItemPageSavedSearchCheck } from './saveSearchCheck';

import { push } from 'connected-react-router';
import TEST_HOUSES from '@/utils/testHouses';

/* ACTION CREATORS */
export const fetchDataForItemPage = (itemId: number) => async (dispatch: Function, getState: Function) => {
    await Promise.all([
        dispatch(fetchItemCardModelIfNeeded(itemId, 'item-page-main')),
        dispatch(fetchItemFacetsIfNeeded([itemId])),
        dispatch(fetchItemDetailsIfNeeded([itemId])),
    ]);

    const { catalogId, catalogStatus, index, isDeleted, sellerId, title } = getItemCardModel(getState(), itemId);

    if (!catalogId || isDeleted) {
        return dispatch(push(getNotFoundUrl()));
    }

    await dispatch(fetchCatalogsIfNeeded({ catalogIds: [catalogId] }));

    const { agentId } = getItemDetail(getState(), itemId);
    const facets = getItemFacets(getState(), itemId);
    const categoryIds = getFacetIds(facets);
    const similarItemInfo = { categoryIds, itemId, sellerId, title };

    const { city, country, state } = getCatalogAddress(getState(), catalogId);

    // we need to load the previous item plus the next 20 items for the item page
    const startingIndexForCatalogCarousel = index === 0 ? 0 : index - 1;

    const query: any = { index: startingIndexForCatalogCarousel, pageSize: 22 };
    if (TEST_HOUSES.includes(sellerId)) {
        query.preview = true;
    }

    const { categories } = facets;
    const firstCategoryId = categories?.[0]?.categoryId;
    const saveSearchParams = getUpcomingTitleSearchParams(title);
    saveSearchParams.status = 'online';

    const promises = [
        dispatch(fetchBidLimitBalanceIfNeeded({ catalogId, itemId })),
        dispatch(fetchCatalogAnnouncementIfNeeded(catalogId, sellerId)),
        dispatch(fetchCatalogCuratorsIfNeeded(catalogId)),
        dispatch(fetchCatalogDetailsIfNeeded([catalogId])),
        dispatch(fetchCatalogItemModels({ catalogId, query })),
        dispatch(fetchDiscoverPageSuggestions(title)),
        dispatch(fetchItemShippingsIfNeeded([itemId])),
        dispatch(fetchListingAgentsIfNeeded([agentId])),
        dispatch(fetchSellerFollowerCountIfNeeded([sellerId])),
        dispatch(fetchSellerRatingSummaryByIdIfNeeded(sellerId)),
        dispatch(fetchSellersDetailsIfNeeded([sellerId])),
        dispatch(fetchTopSellersIfNeeded()),
        dispatch(fetchSimilarItemsIfNeeded(similarItemInfo)),
        dispatch(submitItemPageSavedSearchCheck(facets)),
        dispatch(loadRelatedGeoAreaByLocation(country, city, state)),
        dispatch(submitIsSavedSearchCheck(saveSearchParams)),
    ];

    if (firstCategoryId) {
        promises.push(dispatch(fetchCategoryRelatedSearches(`${firstCategoryId}`)));
    }

    if (catalogStatus === 'live') {
        promises.push(dispatch(fetchCatalogPresence(catalogId)));
        promises.push(dispatch(fetchLiveCatalogStatusIfNeeded(catalogId, sellerId, true)));
    }

    if (catalogStatus === 'done') {
        promises.push(dispatch(fetchSimilarSoldItemsIfNeeded(similarItemInfo)));
        promises.push(dispatch(fetchBidHistoryIfNeeded(itemId)));
    } else {
        promises.push(dispatch(fetchUpcomingCatalogRegistrationIfNeeded(catalogId)));
        promises.push(dispatch(fetchSavedItemCountsIfNeeded([itemId])));
    }

    return Promise.all(promises);
};
